import React, { useState, useCallback, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useToast } from '../contexts/ToastContext';
import {
  IntegrationsPageLayout,
  IntegrationsPageShell,
  AddIntegrationModal,
  ConnectEmailModal,
  DisconnectConfirmModal,
  type Integration,
  type IntegrationTypeOption,
  type ConnectEmailFormData,
  type DisconnectIntegration,
  type IntegrationsPageLayoutLabels,
  type AddIntegrationModalLabels,
  type ConnectEmailModalLabels,
  type DisconnectConfirmModalLabels,
  type ProviderDiscoveryResult,
} from '@hofsync/design-system';
import {
  useIntegrations,
  useConnectEmail,
  useCheckNow,
  useDisconnectIntegration,
  useDiscoverProvider,
  useTestConnection,
  useUpdateSchedule,
  useUpdateSettings,
  useListFolders,
} from '../hooks/useIntegrations';
import type { EmailIntegration } from '../services/integrationsService';
import IntegrationAgentImports from '../components/integrations/IntegrationAgentImports';
import WiberryAgentPanel from '../components/integrations/WiberryAgentPanel';
import { AuthContext } from '../contexts/AuthContext';
import { needsReloginForEmailSetup, sessionCanSetUpEmail } from '../services/integrationGrantService';
import './IntegrationsPage.css';

/** Providers that require manual IMAP activation. */
const PROVIDERS_REQUIRING_IMAP_SETUP = ['gmx', 'web_de', 'freenet', 'gmail', 't_online'];

/**
 * Map API connection_status to design-system IntegrationStatus.
 *
 * #3517: the `default:` arm is the bug this issue records. `paused` is a real server
 * state that stops polling on purpose, and falling through to `error` told the farmer
 * their working-but-parked mailbox was broken — plausibly prompting the next
 * disconnect/reconnect, which re-armed the import the pause existed to stop. It now has
 * its own arm, and the fallback stays `error` for a genuinely unknown status: an
 * unrecognised state is not something to render as healthy.
 */
function mapConnectionStatus(
  status: EmailIntegration['connection_status']
): Integration['status'] {
  switch (status) {
    case 'active':
      return 'connected';
    case 'error':
    case 'disconnected':
      return 'error';
    case 'pending':
    case 'connecting':
      return 'connecting';
    case 'paused':
      return 'paused';
    default:
      return 'error';
  }
}

/**
 * IntegrationsPage - Manage email integrations.
 */
const IntegrationsPage: React.FC = () => {
  const { t } = useTranslation();
  const { showToast } = useToast();
  // #3031: read the context DIRECTLY rather than through `useAuth()`, which throws
  // without an AuthProvider. A purely informational notice must never be able to
  // take the Integrations page down (and the page's own test harness renders
  // without an AuthProvider today).
  const userProfile = useContext(AuthContext)?.userProfile ?? null;

  // The stored grant exists but this BFF session predates it, so /connect and
  // /test will still 403. Surfaced BEFORE the employee tries, which is the
  // difference between a stated trade-off and an opaque failure.
  const showReloginNotice = needsReloginForEmailSetup(userProfile);

  // Modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [showConnectModal, setShowConnectModal] = useState(false);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [integrationToDisconnect, setIntegrationToDisconnect] = useState<DisconnectIntegration | null>(null);
  // #3035: the enumeration state for the ONE open folder editor. Scoped to a single
  // integration deliberately — each fetch costs an IMAP connection, so a per-row cache
  // would invite fetching every mailbox's tree on page load.
  const [folderEditor, setFolderEditor] = useState<{
    integrationId: string;
    availableFolders?: string[];
    /** #3184: wire → decoded labels over offered ∪ persisted, from the same fetch. */
    displayNames?: Record<string, string>;
    isLoading?: boolean;
    errorMessage?: string | null;
  } | null>(null);

  // React Query hooks
  const { data: integrationsData, isLoading, error: fetchError } = useIntegrations();
  const connectMutation = useConnectEmail();
  const discoverMutation = useDiscoverProvider();
  const testConnectionMutation = useTestConnection();
  const checkNowMutation = useCheckNow();
  const updateScheduleMutation = useUpdateSchedule();
  const updateSettingsMutation = useUpdateSettings();
  const listFoldersMutation = useListFolders();
  const disconnectMutation = useDisconnectIntegration();

  // Map API response to design-system Integration type
  const integrations: Integration[] = (integrationsData?.integrations ?? []).map((i) => ({
    id: i.id,
    type: 'email' as const,
    name: i.display_name || i.email_address.split('@')[0],
    account: i.email_address,
    status: mapConnectionStatus(i.connection_status),
    lastSync: i.last_checked_at || undefined,
    lastCheckNewMessages: i.last_poll_emails_fetched ?? undefined,
    error: i.error_message,
    syncSchedule: i.sync_schedule,
    syncScheduleTime: i.sync_schedule_time,
    syncScheduleDay: i.sync_schedule_day,
    extractEmailBodyPdf: i.extract_email_body_pdf,
    // #3035: left `undefined` when a pre-#3035 integration-service omits the field (a
    // rolling deploy), so the card renders no folder row rather than an empty one that
    // would read as "polls nothing".
    pollingFolders: i.polling_folders,
    // #3184: decoded labels for the card summary; absent (pre-#3184 backend) = render raw.
    pollingFolderDisplayNames: i.polling_folder_display_names,
    statusWarnings: i.status_warnings,
  }));

  // Handlers
  const handleAddIntegration = useCallback(() => {
    // #3031: explain up front instead of letting the wizard collect an email address
    // and a mailbox password and THEN 403 at /connect. `sessionCanSetUpEmail` reads the
    // session snapshot the BFF gate actually enforces, so this matches the outcome
    // exactly — including the granted-but-stale case, where the message that helps is
    // "log out and back in", not "ask your administrator".
    // `userProfile &&` on purpose: an unknown profile (still loading, or rendered
    // without an AuthProvider) must NOT lock anyone out of the wizard — the BFF is the
    // real gate, this is only an up-front explanation.
    if (userProfile && !sessionCanSetUpEmail(userProfile)) {
      showToast(
        showReloginNotice
          ? t(
              'integrations.emailDelegation.reloginBody',
              'Dein Betriebsleiter hat dir die E-Mail-Einrichtung freigegeben. Melde dich einmal ab und wieder an, damit die Freigabe wirksam wird.'
            )
          : t(
              'integrations.emailDelegation.notPermitted',
              'Nur Betriebsleiter können E-Mail-Postfächer verbinden. Bitte wende dich an deinen Betriebsleiter.'
            ),
        'error'
      );
      return;
    }
    // Skip integration type selection - go directly to email wizard
    setShowConnectModal(true);
  }, [userProfile, showReloginNotice, showToast, t]);

  const handleSelectType = useCallback((type: IntegrationTypeOption) => {
    if (type === 'email') {
      setShowAddModal(false);
      setShowConnectModal(true);
    }
    // Other types are "coming soon" and won't trigger
  }, []);

  const handleDiscover = useCallback(async (email: string): Promise<ProviderDiscoveryResult | null> => {
    try {
      const result = await discoverMutation.mutateAsync(email);
      const provider = result.provider;
      const needsImapSetup = PROVIDERS_REQUIRING_IMAP_SETUP.includes(provider);

      return {
        provider,
        providerName: result.provider_name,
        // #3253: `oauth_url` is deliberately NOT forwarded. The server still reports it,
        // but the wizard refuses OAuth providers rather than starting a consent flow, so
        // there is nothing in the browser entitled to act on the URL.
        requiresOAuth: result.requires_oauth,
        imapSetupSteps: needsImapSetup
          ? (t(`integrations.providerInstructions.${provider}.steps`, { returnObjects: true }) as string[])
          : undefined,
        imapSetupHelpUrl: needsImapSetup
          ? t(`integrations.providerInstructions.${provider}.helpUrl`)
          : undefined,
        // #1852: surface any discovered IMAP host so the host field can prefill it
        // — still editable, never blocking.
        imapHost: result.imap_host ?? undefined,
        // #3032: the server decides whether the derived host may be overridden
        // (true for every password provider). The modal keys the host field off it.
        // A null/absent value means a pre-#3032 backend made no decision — collapse
        // it to `undefined` so the modal's `?? provider === 'unknown'` fallback fires
        // instead of reading it as an explicit "not allowed".
        allowManualOverride: result.allow_manual_override ?? undefined,
      };
    } catch (err) {
      // Let the modal handle the error
      throw err;
    }
  }, [discoverMutation, t]);

  const handleTestConnection = useCallback(async (
    email: string,
    password: string,
    imap?: { imapHost?: string; imapPort?: number },
  ) => {
    try {
      const result = await testConnectionMutation.mutateAsync({
        email,
        password,
        // #1852: manual IMAP host for provider === 'unknown' (port fixed at 993).
        imapHost: imap?.imapHost,
        imapPort: imap?.imapPort,
      });
      return {
        success: result.success,
        errorMessage: result.error_message ?? undefined,
        // #3035: the folder tree the server enumerated on the same credentials. `null` and
        // absent both collapse to `undefined` so the modal's `?? 'error'` fallback fires —
        // a pre-#3035 backend must read as "we learned nothing", not as "no folders".
        folderEnumeration: result.folder_enumeration ?? undefined,
        folders: result.folders,
        suggestedFolders: result.suggested_folders,
        // #3184: decoded labels for the picker; the modal submits wire names regardless.
        displayNames: result.display_names,
      };
    } catch (err) {
      // Let the modal handle the error
      throw err;
    }
  }, [testConnectionMutation]);

  // #3253: `handleOAuthRedirect` lived here. It POSTed /api/oauth/authorize and sent the
  // browser to the provider's consent screen — a flow that completed and then produced
  // nothing, because the generic /api/oauth/callback creates an OAuthConnection row and
  // never an email Integration. It also wrote a `pending_email_oauth` sessionStorage key
  // that nothing in the tree ever read, and sniffed the provider with a hostname check
  // (`.microsoft.com`) that `login.microsoftonline.com` fails, so the Microsoft flow was
  // labelled `google`. The wizard now refuses OAuth providers outright; reviving this
  // needs the full ship design tracked in the follow-up issue, not a restored callback.

  const handleConnect = useCallback(async (data: ConnectEmailFormData) => {
    try {
      await connectMutation.mutateAsync({
        email_address: data.emailAddress,
        password: data.password,
        display_name: data.name || undefined,
        // #1852: manual IMAP host/port for provider === 'unknown' (undefined for
        // auto-detected providers → backend keeps yaml-authoritative config).
        imap_host: data.imapHost,
        imap_port: data.imapPort,
        // #3035: the folders the farmer picked. The key is OMITTED, not sent as null, when
        // the tree could not be enumerated — omission is what tells the server to keep its
        // own INBOX+Sent default, and it refuses an explicitly empty list by design.
        ...(data.folders ? { folders: data.folders } : {}),
        // #3253: the backfill window, same omit-unless-chosen rule as `folders`. The
        // guard checks `undefined` explicitly rather than truthiness, because the only
        // thing that may suppress the key is "the farmer did not choose" — `'none'`
        // ("import no history") is a real choice, and any guard that grows to treat an
        // empty-ish value as absent would hand them the `last_10` default they were
        // trying to avoid, which is the exact stranding this issue fixes.
        ...(data.historicalImport !== undefined ? { historical_import: data.historicalImport } : {}),
      });
      showToast(t('integrations.connectSuccess', 'Email connected successfully'), 'success');
    } catch (err: unknown) {
      // Check for rate limit error (429)
      const axiosError = err as { response?: { status?: number } };
      if (axiosError.response?.status === 429) {
        showToast(t('integrations.errors.rateLimitExceeded', 'Please wait a minute and try again.'), 'warning');
        throw err;
      }
      // #3035: prefer the upstream `detail`. /connect answers 400 with a German explanation
      // when the folder selection is rejected (empty list, >100 folders, an unusable name);
      // `err.message` on a raw AxiosError is only "Request failed with status code 400",
      // which tells the farmer nothing about what to change.
      const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
      const message =
        typeof detail === 'string' && detail
          ? detail
          : err instanceof Error
            ? err.message
            : t('integrations.errors.connectionFailed');
      showToast(message, 'error');
      throw err; // Re-throw so modal shows error
    }
  }, [connectMutation, showToast, t]);

  const handleCheckNow = useCallback((id: string) => {
    checkNowMutation.mutate(id, {
      onSuccess: (data) => {
        // #3431: only a `completed` status carries real counts. Anything else is the 202's
        // `started` — or, during a rolling deploy, a payload from a PRE-#3431 BFF that
        // turned the new upstream's 202 into `{"detail": "Failed to trigger email check"}`
        // and so has no `status` at all. Defaulting to the counts branch would render
        // `undefined > 0` as "No new emails found": a confident false statement about work
        // that has not started, which is the same class of wrong answer as the 500 this
        // replaced. So the counts branch is opt-in, not the fallback.
        if (data.status !== 'completed') {
          showToast(
            t('integrations.checkStarted', 'Check started. On a large mailbox this can take a few minutes.'),
            'info'
          );
          return;
        }
        const message = data.emails_fetched > 0
          ? t('integrations.checkSuccess', 'Found {{count}} new emails', { count: data.emails_fetched })
          : t('integrations.checkSuccessNoNew', 'No new emails found');
        showToast(message, 'success');
      },
      onError: (err: unknown) => {
        // Check for rate limit error (429)
        const axiosError = err as { response?: { status?: number } };
        if (axiosError.response?.status === 429) {
          showToast(t('integrations.errors.rateLimitExceeded', 'Please wait a minute and try again.'), 'warning');
          return;
        }
        // #3431: 409 means a sync is genuinely already running — information, not a
        // failure. Showing it as an error is what convinces a farmer his mailbox is
        // broken, and the disconnect/reconnect that follows re-arms a full import.
        if (axiosError.response?.status === 409) {
          showToast(
            t('integrations.errors.checkAlreadyRunning', 'A check is already running for this mailbox.'),
            'info'
          );
          return;
        }
        const message = err instanceof Error ? err.message : t('integrations.errors.checkFailed', 'Check failed');
        showToast(message, 'error');
      },
    });
  }, [checkNowMutation, showToast, t]);

  // Which integrations are currently being checked.
  //
  // #3431: the mutation is only pending for the ~50ms the 202 takes, so on its own the
  // spinner vanished before the poll had even started — the farmer saw nothing happen and
  // clicked again, which is the loop this issue is about. The authoritative signal is each
  // integration's own `check_status`, which stays `running` until its background poll
  // finishes; the mutation flag still covers the gap before the first refetch lands.
  //
  // A LIST, not one id: several mailboxes can genuinely be checking at once (the cap is
  // five per farmer), and picking the first would spin one card while the others showed an
  // enabled button that only 409s — the same click-again loop, one card over.
  const checkingIntegrationId = checkNowMutation.isPending ? checkNowMutation.variables : null;
  const checkingIntegrationIds = (integrationsData?.integrations ?? [])
    .filter((i) => i.check_status?.status === 'running')
    .map((i) => i.id);

  const handleScheduleChange = useCallback((id: string, mode: string, time?: string, day?: number) => {
    updateScheduleMutation.mutate(
      {
        integrationId: id,
        schedule: {
          sync_schedule: mode as 'automatic' | 'daily' | 'weekly',
          sync_schedule_time: time,
          sync_schedule_day: day,
        },
      },
      {
        onSuccess: () => {
          showToast(t('integrations.scheduleUpdated', 'Sync schedule updated'), 'success');
        },
        onError: (err: unknown) => {
          const message = err instanceof Error ? err.message : t('integrations.errors.scheduleUpdateFailed', 'Failed to update schedule');
          showToast(message, 'error');
        },
      }
    );
  }, [updateScheduleMutation, showToast, t]);

  const handleExtractEmailBodyPdfChange = useCallback((id: string, enabled: boolean) => {
    updateSettingsMutation.mutate(
      {
        integrationId: id,
        settings: { extract_email_body_pdf: enabled },
      },
      {
        onSuccess: () => {
          showToast(t('integrations.settingsUpdated', 'Settings updated'), 'success');
        },
        onError: (err: unknown) => {
          // The /settings endpoint shares the schedule rate-limit bucket; surface the
          // same friendly message as the other rate-limited actions (checkNow/disconnect).
          const axiosError = err as { response?: { status?: number } };
          if (axiosError.response?.status === 429) {
            showToast(t('integrations.errors.rateLimitExceeded', 'Please wait a minute and try again.'), 'warning');
            return;
          }
          const message = err instanceof Error ? err.message : t('integrations.errors.settingsUpdateFailed', 'Failed to update settings');
          showToast(message, 'error');
        },
      }
    );
  }, [updateSettingsMutation, showToast, t]);

  // #3035: fetch the folder tree when the editor opens. An `error`/`empty` outcome is
  // rendered in the editor rather than thrown — the card explains what happened and leaves
  // the current selection untouched.
  const handleEditFolders = useCallback((id: string) => {
    setFolderEditor({ integrationId: id, isLoading: true, errorMessage: null });
    listFoldersMutation.mutate(id, {
      onSuccess: (data) => {
        setFolderEditor({
          integrationId: id,
          isLoading: false,
          availableFolders: data.folders,
          // #3184: labels for the editor (covers offered ∪ persisted); absent = raw.
          displayNames: data.display_names,
          errorMessage:
            data.outcome === 'resolved'
              ? null
              : data.outcome === 'empty'
                ? t(
                    'integrations.folders.emptyMailbox',
                    'In diesem Postfach wurde kein abrufbarer Ordner gefunden.'
                  )
                : data.error_message ||
                  t('integrations.folders.loadFailed', 'Die Ordnerliste konnte nicht abgerufen werden.'),
        });
      },
      onError: (err: unknown) => {
        const message =
          err instanceof Error
            ? err.message
            : t('integrations.folders.loadFailed', 'Die Ordnerliste konnte nicht abgerufen werden.');
        setFolderEditor({ integrationId: id, isLoading: false, availableFolders: [], errorMessage: message });
      },
    });
  }, [listFoldersMutation, t]);

  const handleFoldersChange = useCallback((id: string, folders: string[]) => {
    updateSettingsMutation.mutate(
      { integrationId: id, settings: { folders } },
      {
        onSuccess: () => {
          setFolderEditor(null);
          showToast(t('integrations.folders.updated', 'Ordnerauswahl gespeichert'), 'success');
        },
        onError: (err: unknown) => {
          const axiosError = err as { response?: { status?: number } };
          if (axiosError.response?.status === 429) {
            showToast(t('integrations.errors.rateLimitExceeded', 'Please wait a minute and try again.'), 'warning');
            return;
          }
          // #3035: the folder validation's 400 carries a German `detail` the BFF forwards
          // verbatim; a raw AxiosError's `.message` is only "Request failed with status
          // code 400".
          const detail = (err as { response?: { data?: { detail?: unknown } } }).response?.data?.detail;
          const message =
            typeof detail === 'string' && detail
              ? detail
              : err instanceof Error
                ? err.message
                : t('integrations.errors.settingsUpdateFailed', 'Failed to update settings');
          showToast(message, 'error');
        },
      }
    );
  }, [updateSettingsMutation, showToast, t]);

  const handleReconnect = useCallback((id: string) => {
    // For now, reconnect opens the connect modal
    // In the future, this could open a specific reconnect flow
    const integration = integrationsData?.integrations.find((i) => i.id === id);
    if (integration) {
      setShowConnectModal(true);
    }
  }, [integrationsData]);

  const handleDisconnect = useCallback((id: string) => {
    const integration = integrations.find((i) => i.id === id);
    if (integration) {
      setIntegrationToDisconnect({
        id: integration.id,
        type: integration.type,
        name: integration.name,
        account: integration.account,
      });
      setShowDisconnectModal(true);
    }
  }, [integrations]);

  const handleConfirmDisconnect = useCallback((id: string) => {
    disconnectMutation.mutate(id, {
      onSuccess: () => {
        showToast(t('integrations.disconnectSuccess', 'Integration disconnected'), 'success');
        setShowDisconnectModal(false);
        setIntegrationToDisconnect(null);
      },
      onError: (err: unknown) => {
        // Check for rate limit error (429)
        const axiosError = err as { response?: { status?: number } };
        if (axiosError.response?.status === 429) {
          showToast(t('integrations.errors.rateLimitExceeded', 'Please wait a minute and try again.'), 'warning');
          return;
        }
        const message = err instanceof Error ? err.message : t('integrations.errors.disconnectFailed', 'Disconnect failed');
        showToast(message, 'error');
      },
    });
  }, [disconnectMutation, showToast, t]);

  // Labels for i18n
  const pageLabels: IntegrationsPageLayoutLabels = {
    // No `title` (#3480): the section's heading is passed explicitly as the
    // `title` PROP below. Leaving it here would give the <h2> a second source
    // whose fallback is `integrations.title` — the shell's own <h1> text — so a
    // missing `integrations.mailboxesTitle` would silently restore the
    // "which heading owns this page" ambiguity this PR removes.
    description: t('integrations.description'),
    addButton: t('integrations.addEmailInbox'),
    emptyTitle: t('integrations.empty.title', 'Noch keine Postfächer verbunden'),
    emptyDescription: t(
      'integrations.empty.description',
      'Verbinde ein E-Mail-Postfach, damit Hofsync eingehende Dokumente daraus automatisch übernimmt.'
    ),
    emptyButton: t('integrations.empty.button', 'Erstes Postfach verbinden'),
    integrationCard: {
      checkNow: t('integrations.checkNow'),
      checking: t('integrations.checking', 'Checking...'),
      reconnect: t('integrations.reconnect'),
      disconnect: t('integrations.disconnect'),
      lastChecked: t('integrations.lastChecked'),
      noNewMessages: t('integrations.noNewMessages', 'No new messages received'),
      newMessagesReceived: t('integrations.newMessagesReceived', 'new messages received'),
      // #3517: a mailbox support has parked. The explanation is not decoration — the
      // incident this state comes from turned on a farmer reading a parked mailbox as a
      // broken one and reconnecting, which re-armed the import the pause had stopped.
      // English fallbacks, matching every neighbour in this block. The German lives in
      // de.json; a German literal here would surface as German text to an English-locale
      // user if the key were ever dropped or a locale bundle loaded incompletely.
      paused: t('integrations.paused', 'Paused'),
      pausedExplanation: t(
        'integrations.pausedExplanation',
        'Email collection is paused by support. No emails are being collected right now.'
      ),
      // Relative time labels
      never: t('integrations.time.never', 'Never'),
      justNow: t('integrations.time.justNow', 'Just now'),
      minuteAgo: t('integrations.time.minuteAgo', '1 minute ago'),
      minutesAgo: t('integrations.time.minutesAgo', '{count} minutes ago'),
      hourAgo: t('integrations.time.hourAgo', '1 hour ago'),
      hoursAgo: t('integrations.time.hoursAgo', '{count} hours ago'),
      dayAgo: t('integrations.time.dayAgo', '1 day ago'),
      daysAgo: t('integrations.time.daysAgo', '{count} days ago'),
      // Schedule labels
      scheduleLabel: t('integrations.schedule.label', 'Sync schedule'),
      scheduleAutomatic: t('integrations.schedule.automatic', 'Automatisch'),
      scheduleDaily: t('integrations.schedule.daily', 'Einmal täglich'),
      scheduleWeekly: t('integrations.schedule.weekly', 'Einmal wöchentlich'),
      scheduleTimeLabel: t('integrations.schedule.timeLabel', 'Uhrzeit'),
      scheduleDayLabel: t('integrations.schedule.dayLabel', 'Tag'),
      scheduleDays: [
        t('integrations.schedule.days.monday', 'Montag'),
        t('integrations.schedule.days.tuesday', 'Dienstag'),
        t('integrations.schedule.days.wednesday', 'Mittwoch'),
        t('integrations.schedule.days.thursday', 'Donnerstag'),
        t('integrations.schedule.days.friday', 'Freitag'),
        t('integrations.schedule.days.saturday', 'Samstag'),
        t('integrations.schedule.days.sunday', 'Sonntag'),
      ],
      scheduleAutomaticDesc: t('integrations.schedule.automaticDesc', 'Häufige Prüfung in Geschäftszeiten, seltener abends und am Wochenende.'),
      scheduleDailyDesc: t('integrations.schedule.dailyDesc', 'Jeden Tag um {time} Uhr.'),
      scheduleWeeklyDesc: t('integrations.schedule.weeklyDesc', 'Jeden {day} um {time} Uhr.'),
      scheduleEdit: t('integrations.schedule.edit', 'Bearbeiten'),
      scheduleDone: t('integrations.schedule.done', 'Fertig'),
      extractEmailBodyPdf: t('integrations.extractEmailBodyPdf', 'E-Mail-Text als separates PDF ablegen'),
      // #3035: polled-folder coverage. "Es werden 2 von 14 Ordnern geprüft" is the signal
      // that makes a mis-targeted mailbox self-diagnosing instead of looking like a quiet one.
      foldersLabel: t('integrations.folders.label', 'Geprüfte Ordner'),
      foldersCoverage: t('integrations.folders.coverage', 'Es werden {count} von {total} Ordnern geprüft'),
      foldersNoneWarning: t(
        'integrations.folders.noneWarning',
        'Es werden keine Ordner geprüft — aus diesem Postfach können keine Dokumente ankommen.'
      ),
      // #3545: strictly weaker than the line above — das Postfach wird geprüft, einzelne
      // Ordner lassen sich aber nicht öffnen. Ohne diesen Hinweis meldet das Postfach
      // "in Ordnung" und liefert aus diesen Ordnern trotzdem nichts.
      foldersUnreadableWarning: t(
        'integrations.folders.unreadableWarning',
        'Auf einzelne Ordner konnte nicht zugegriffen werden — der Mailserver hat den Zugriff ' +
          'abgelehnt, aus ihnen können keine Dokumente ankommen.'
      ),
      foldersEdit: t('integrations.folders.edit', 'Ordner bearbeiten'),
      foldersSave: t('integrations.folders.save', 'Speichern'),
      foldersCancel: t('common.cancel', 'Abbrechen'),
      foldersLoading: t('integrations.folders.loading', 'Ordner werden geladen...'),
      foldersRequired: t('integrations.folders.required', 'Bitte wähle mindestens einen Ordner aus.'),
    },
  };

  const addModalLabels: AddIntegrationModalLabels = {
    title: t('integrations.addIntegration.title'),
    comingSoon: t('integrations.addIntegration.comingSoon'),
  };

  const connectModalLabels: ConnectEmailModalLabels = {
    title: t('integrations.connectEmail.title'),
    emailAddressLabel: t('integrations.connectEmail.emailAddress'),
    emailAddressPlaceholder: t('integrations.connectEmail.emailPlaceholder'),
    nameLabel: t('integrations.connectEmail.name'),
    namePlaceholder: t('integrations.connectEmail.namePlaceholder'),
    nameHint: t('integrations.connectEmail.nameHint'),
    passwordLabel: t('integrations.connectEmail.password'),
    back: t('integrations.connectEmail.back'),
    continue: t('integrations.connectEmail.continue'),
    connect: t('integrations.connectEmail.connect'),
    connecting: t('integrations.connectEmail.connecting'),
    discovering: t('integrations.connectEmail.discovering'),
    testingConnection: t('integrations.connectEmail.testingConnection', 'Verbindung wird getestet...'),
    errorEmailRequired: t('integrations.errors.emailRequired'),
    errorEmailInvalid: t('integrations.errors.emailInvalid'),
    errorPasswordRequired: t('integrations.errors.passwordRequired'),
    errorDiscoveryFailed: t('integrations.errors.discoveryFailed'),
    errorConnectionFailed: t('integrations.errors.connectionFailed', 'Verbindung fehlgeschlagen. Bitte überprüfe deine Zugangsdaten.'),
    imapSetupTitle: t('integrations.providerInstructions.title'),
    imapSetupSubtitle: t('integrations.providerInstructions.subtitle'),
    imapSetupDoneButton: t('integrations.providerInstructions.doneButton'),
    imapSetupHelpLinkText: t('integrations.providerInstructions.helpLinkText'),
    showPassword: t('integrations.connectEmail.showPassword', 'Show password'),
    hidePassword: t('integrations.connectEmail.hidePassword', 'Hide password'),
    // #1852: manual IMAP host (provider === 'unknown'); #3032 adds the
    // override hint shown when a provider WAS detected.
    manualImapBanner: t('integrations.connectEmail.manualImapBanner'),
    imapHostOverrideHint: t('integrations.connectEmail.imapHostOverrideHint'),
    imapHostLabel: t('integrations.connectEmail.imapHostLabel'),
    imapHostPlaceholder: t('integrations.connectEmail.imapHostPlaceholder'),
    imapPortLabel: t('integrations.connectEmail.imapPortLabel'),
    errorImapHostRequired: t('integrations.errors.imapHostRequired'),
    // #3035: the folder-selection step.
    folderStepTitle: t('integrations.connectEmail.folderStepTitle', 'Welche Ordner sollen geprüft werden?'),
    folderStepSubtitle: t(
      'integrations.connectEmail.folderStepSubtitle',
      'Wir lesen nur die hier ausgewählten Ordner. Wenn eingehende Post in Unterordner einsortiert wird, wähle diese ebenfalls aus.'
    ),
    folderStepEnumerationFailed: t(
      'integrations.connectEmail.folderStepEnumerationFailed',
      'Die Ordnerliste konnte nicht von deinem E-Mail-Server abgerufen werden. Es werden dein Posteingang und dein Gesendet-Ordner geprüft; du kannst das später anpassen.'
    ),
    folderStepEmpty: t(
      'integrations.connectEmail.folderStepEmpty',
      'In diesem Postfach wurde kein abrufbarer Ordner gefunden. Bitte prüfe das Konto und versuche es erneut.'
    ),
    folderStepCoverage: t('integrations.connectEmail.folderStepCoverage', '{selected} von {total} Ordnern ausgewählt'),
    folderSelectAll: t('integrations.connectEmail.folderSelectAll', 'Alle auswählen'),
    folderSelectNone: t('integrations.connectEmail.folderSelectNone', 'Auswahl aufheben'),
    errorFolderRequired: t('integrations.errors.folderRequired', 'Bitte wähle mindestens einen Ordner aus.'),
    // #3253: the historical-import section, inside the folder step.
    historicalImportTitle: t(
      'integrations.connectEmail.historicalImportTitle',
      'Wie viel Verlauf sollen wir importieren?'
    ),
    historicalImportSubtitle: t(
      'integrations.connectEmail.historicalImportSubtitle',
      'Neu eingehende E-Mails werden immer verarbeitet. Dies legt nur fest, wie weit wir in den ausgewählten Ordnern zurückschauen.'
    ),
    historicalImportNone: t('integrations.connectEmail.historicalImportNone', 'Keine bisherigen E-Mails'),
    historicalImportLast10: t(
      'integrations.connectEmail.historicalImportLast10',
      'Nur die 10 neuesten E-Mails'
    ),
    historicalImportLast100: t(
      'integrations.connectEmail.historicalImportLast100',
      'Die 100 neuesten E-Mails'
    ),
    historicalImportOneYear: t('integrations.connectEmail.historicalImportOneYear', 'Das vergangene Jahr'),
    historicalImportThreeYears: t(
      'integrations.connectEmail.historicalImportThreeYears',
      'Die vergangenen drei Jahre'
    ),
    // #3253: the honest refusal that replaced the dead consent redirect.
    oauthUnsupportedTitle: t(
      'integrations.connectEmail.oauthUnsupportedTitle',
      'Dieser E-Mail-Anbieter wird noch nicht unterstützt'
    ),
    oauthUnsupportedBody: t(
      'integrations.connectEmail.oauthUnsupportedBody',
      'Postfächer von {provider} lassen sich nur über eine Anmeldung beim Anbieter verbinden — das unterstützen wir noch nicht. Ein Postfach, das du mit einem E-Mail-Passwort verbinden kannst, funktioniert bereits.'
    ),
  };

  const disconnectModalLabels: DisconnectConfirmModalLabels = {
    title: t('integrations.disconnectTitle'),
    message: t('integrations.disconnectMessage'),
    cancel: t('common.cancel', 'Cancel'),
    disconnect: t('integrations.disconnect'),
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="integrations-page integrations-page--loading">
        <div className="integrations-page__spinner" />
      </div>
    );
  }

  // Error state
  if (fetchError) {
    return (
      <div className="integrations-page integrations-page--error">
        <p>{t('integrations.errors.loadFailed', 'Failed to load integrations')}</p>
      </div>
    );
  }

  return (
    <>
      {showReloginNotice && (
        <div className="integrations-page__relogin-notice" role="status">
          <span className="integrations-page__relogin-notice-title">
            {t('integrations.emailDelegation.reloginTitle', 'Freigabe erteilt — Neuanmeldung erforderlich')}
          </span>
          <span className="integrations-page__relogin-notice-body">
            {t(
              'integrations.emailDelegation.reloginBody',
              'Dein Betriebsleiter hat dir die E-Mail-Einrichtung freigegeben. Melde dich einmal ab und wieder an, damit die Freigabe wirksam wird.'
            )}
          </span>
        </div>
      )}

      {/* #3480 — a real page shell. The h1, the page description and the page
          chrome live HERE, not inside the mailbox template. Before this, that
          template owned all three and so spoke on behalf of the two unrelated
          sections stacked below it — which is how the page came to render
          "Noch keine Integrationen" directly above a successfully processed
          import. Each section now owns its own heading, state and empty state,
          and can only ever speak for itself.

          No page-level action: all three sections own their own primary action
          (connect a mailbox / nothing to add / pair a device), and a shared
          button would have to fan out to three unrelated flows. */}
      <IntegrationsPageShell
        title={t('integrations.title', 'Integrationen')}
        description={t(
          'integrations.pageDescription',
          'Postfächer, Portal-Importe und der Sync-Agent — alle Datenquellen an einem Ort.'
        )}
      >
      <IntegrationsPageLayout
        renderAs="section"
        title={t('integrations.mailboxesTitle', 'E-Mail-Postfächer')}
        integrations={integrations}
        checkingIntegrationId={checkingIntegrationId}
        checkingIntegrationIds={checkingIntegrationIds}
        onAddIntegration={handleAddIntegration}
        onCheckNow={handleCheckNow}
        onReconnect={handleReconnect}
        onDisconnect={handleDisconnect}
        onScheduleChange={handleScheduleChange}
        onExtractEmailBodyPdfChange={handleExtractEmailBodyPdfChange}
        onEditFolders={handleEditFolders}
        onFoldersChange={handleFoldersChange}
        folderEditor={folderEditor}
        labels={pageLabels}
      />

      {/* #1936 — harvest years imported from connected portals. Self-contained:
          owns its query, loading and empty state. Renamed "Portal-Importe" in
          #3480: it was "Integration Agent", which collided with the Sync-Agent
          below for anyone who had not read both issues. It is named for what it
          does; "Agent" is now reserved for the thing that genuinely is one. */}
      <IntegrationAgentImports />

      {/* #3071 — the paired wiberry device: pairing, tokens, agents, run
          history. Self-contained. Distinct from Portal-Importe above — a
          different feature with a different lifecycle (pair-a-device vs
          review-imports), which the section headings now carry instead of a
          comment. #3068/#1936 path-collision risk noted in #3071's context
          manifest; this section deliberately sits clear of it. */}
      <WiberryAgentPanel />
      </IntegrationsPageShell>

      <AddIntegrationModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSelectType={handleSelectType}
        labels={addModalLabels}
      />

      <ConnectEmailModal
        isOpen={showConnectModal}
        onClose={() => setShowConnectModal(false)}
        onConnect={handleConnect}
        onDiscover={handleDiscover}
        onTestConnection={handleTestConnection}
        labels={connectModalLabels}
      />

      <DisconnectConfirmModal
        isOpen={showDisconnectModal}
        onClose={() => {
          setShowDisconnectModal(false);
          setIntegrationToDisconnect(null);
        }}
        onConfirm={handleConfirmDisconnect}
        integration={integrationToDisconnect}
        labels={disconnectModalLabels}
      />
    </>
  );
};

export default IntegrationsPage;
